import React, {Fragment, useEffect, useRef, useState} from "react";
import {Link, useLocation, useParams} from "react-router-dom";
import AsyncSelect from "react-select/async";
import {Badge, Card, CardBody, Col, Container, Row, Table} from "reactstrap";
import Breadcrumbs from "../../layout/breadcrumb";
import {getWorkflowApi, searchWorkflowProductsApi, testWorkflowApi} from "../../services/tagsAutomationService";
import {BASE, errorMessage, RequirePermission, TagBadge, useCan} from "./common";
import "./tagsAutomation.scss";

/**
 * GOD-TASK-197 Tags Automation › Test a workflow on one product (prototype screen 4). Writes
 * nothing; Shopify's current tags are only read.
 */
const TestWorkflow = () => {
    const {id} = useParams();
    const can = useCan();
    const location = useLocation();
    const [workflow, setWorkflow] = useState(null);
    const [product, setProduct] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const searchTimer = useRef(null);

    const evaluate = (productId) => {
        setLoading(true);
        setError(null);
        setShowAll(false);
        testWorkflowApi(id, productId)
            .then(setResult)
            .catch(e => {
                setResult(null);
                setError(errorMessage(e));
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!id) return;   // exit transition: params are empty for a moment
        getWorkflowApi(id).then(setWorkflow).catch(e => setError(errorMessage(e)));
        const productId = new URLSearchParams(location.search).get('product_id');
        if (productId) {
            evaluate(productId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Search waits until typing pauses.
    const loadProducts = (input, callback) => {
        clearTimeout(searchTimer.current);
        if (!input) {
            callback([]);
            return;
        }
        searchTimer.current = setTimeout(() => {
            searchWorkflowProductsApi(input)
                .then(rows => callback(rows.map(p => ({value: p.id, label: `${p.code} · ${p.name}`, product: p}))))
                .catch(() => callback([]));
        }, 300);
    };

    const name = workflow ? workflow.name : '…';
    const tagsAfter = result ? result.tags_after : [];
    const added = result ? result.adds.map(t => t.toLowerCase()) : [];
    const visibleTags = showAll ? tagsAfter : tagsAfter.slice(0, 10);

    const pass = (ok, yes, no) => <Badge color={ok ? 'success' : 'danger'}>{ok ? yes : no}</Badge>;
    const dataMenu = [
        {type: 1, text: 'Tags Automation', link: '/tags-automation/workflows'},
        {type: 1, text: name, link: can('Update Workflow') ? `/tags-automation/workflows/${id}/edit` : '/tags-automation/workflows'},
        {type: 0, text: 'Test'},
    ];

    return (
        <Fragment>
            <Breadcrumbs title={`Test workflow: ${name}`} data={dataMenu}/>
            <Container fluid={true} className="product-wrapper">
                <div className="d-flex justify-content-end mb-3">
                    {can('Update Workflow')
                        ? <Link className="btn btn-sm btn-outline-primary" to={`${BASE}/workflows/${id}/edit`}><i className="fa fa-arrow-left"/> Back to workflow</Link>
                        : <Link className="btn btn-sm btn-outline-primary" to={`${BASE}/workflows`}><i className="fa fa-arrow-left"/> Back to workflows</Link>}
                </div>

                {error && <div className="alert alert-danger">{error}</div>}

                <Card>
                    <CardBody>
                        <h5 className="mb-2">Pick a product</h5>
                        <p className="f-12 text-muted">Nothing is written to Shopify. This evaluates the workflow against the product's marketplace data right now, condition by condition, then compares the result with the tags Shopify currently holds for the product to say whether a write would be needed.</p>
                        <Row>
                            <Col md={9}>
                                <AsyncSelect
                                    placeholder="Style code or product title"
                                    loadOptions={loadProducts}
                                    value={product ? {value: product.id, label: `${product.code} · ${product.name}`} : null}
                                    onChange={o => setProduct(o ? o.product : null)}
                                    isClearable
                                    noOptionsMessage={({inputValue}) => inputValue ? 'No product found' : 'Type a style code or title'}
                                    components={{IndicatorSeparator: () => null}}
                                />
                            </Col>
                            <Col md={3}>
                                <button className="btn btn-primary btn-block" type="button" disabled={!product || loading} onClick={() => evaluate(product.id)}>
                                    {loading ? 'Evaluating…' : 'Evaluate'}
                                </button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {result && (
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-start mb-3" style={{gap: 12}}>
                                <div>
                                    <h5 className="mb-1">{result.product.code} · {result.product.name}</h5>
                                    <div className="f-12 text-muted">{result.product.summary}</div>
                                </div>
                                {pass(result.matched, 'All conditions match', 'Conditions do not match')}
                            </div>
                            <Table responsive className="mb-3">
                                <thead>
                                <tr><th style={{width: 80}}/><th>Condition</th><th style={{width: 260}}>Product value</th><th style={{width: 110}}>Result</th></tr>
                                </thead>
                                <tbody>
                                {result.conditions.length === 0 && <tr><td/><td>No conditions: matches every product</td><td/><td>{pass(true, 'Pass')}</td></tr>}
                                {result.conditions.map((c, i) => (
                                    <tr key={i}>
                                        <td className="f-12 f-w-600 text-muted">{c.join}</td>
                                        <td>
                                            <span className="badge ta-badge badge-light-info">{c.variant_match ? (c.variant_match === 'all' ? 'All of ' : 'Any of ') : ''}{c.group} → {c.field_label}</span>
                                            {' '}{c.operator_label}{' '}
                                            {c.value_label !== '' && <span className="badge ta-badge badge-light">{c.value_label}</span>}
                                        </td>
                                        <td>{c.actual}</td>
                                        <td>{pass(c.pass, 'Pass', 'Fail')}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                            <Row className="mb-2 align-items-center">
                                <Col md={2} className="f-12 text-muted">Workflow result</Col>
                                <Col md={10} className="d-flex align-items-center flex-wrap" style={{gap: 6}}>
                                    {pass(result.matched, 'Matches', 'No match')}
                                    {result.adds.length > 0 && <><span className="ml-1">adds</span>{result.adds.map(t => <TagBadge key={t} tag={t} kind="add"/>)}</>}
                                    {result.removes.length > 0 && <><span className="ml-1">removes</span>{result.removes.map(t => <span key={t} className="badge ta-badge badge-light-danger"><del>{t}</del></span>)}</>}
                                </Col>
                            </Row>
                            <Row className="mb-2 align-items-center">
                                <Col md={2} className="f-12 text-muted">Shopify today</Col>
                                <Col md={10} className="d-flex align-items-center flex-wrap" style={{gap: 6}}>
                                    {!result.shopify.read ? "Could not read the product's tags from Shopify."
                                        : result.shopify.already_has.length > 0 ? <>Already carries {result.shopify.already_has.map(t => <TagBadge key={t} tag={t}/>)}</>
                                            : result.adds.length > 0 ? <>Does not carry {result.adds.map(t => <TagBadge key={t} tag={t}/>)}</>
                                                : 'Tags read'}
                                    <span className="f-12 text-muted">(live tags fetched {result.shopify.fetched_at})</span>
                                </Col>
                            </Row>
                            <Row className="mb-3 align-items-center">
                                <Col md={2} className="f-12 text-muted">Write needed</Col>
                                <Col md={10}>
                                    <TagBadge tag={result.shopify.write_needed === null ? 'Unknown: Shopify could not be read' : result.shopify.write_needed ? 'Yes, the next sync writes the new tags' : 'No change required'}/>
                                </Col>
                            </Row>
                            <div className="d-flex align-items-center flex-wrap" style={{gap: 6}}>
                                <span className="f-12 text-muted">Tags after this workflow ({tagsAfter.length}):</span>
                                {visibleTags.map(t => <TagBadge key={t} tag={t} kind={added.includes(t.toLowerCase()) ? 'add' : undefined}/>)}
                                {tagsAfter.length > 10 && (
                                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowAll(!showAll)}>{showAll ? 'Show fewer' : `+ ${tagsAfter.length - 10} more`}</button>
                                )}
                            </div>
                        </CardBody>
                    </Card>
                )}

                {result && (
                    <Card>
                        <CardBody>
                            <h5 className="mb-1">Same product, other active workflows</h5>
                            <div className="f-12 text-muted mb-3">Workflows run in order. The final tag set is what reaches Shopify.</div>
                            {result.other_workflows.length === 0 ? <p className="f-12 text-muted mb-0">No other active workflows.</p> : (
                                <Table responsive className="mb-0">
                                    <tbody>
                                    {result.other_workflows.map(o => (
                                        <tr key={o.id}>
                                            <td className="f-w-500" style={{width: '30%'}}>{o.name}</td>
                                            <td>{o.reason}</td>
                                            <td style={{width: 200}}><Badge color={o.matched ? 'success' : 'danger'}>{o.effect}</Badge></td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </Table>
                            )}
                        </CardBody>
                    </Card>
                )}
            </Container>
        </Fragment>
    );
};

const TestWorkflowPage = () => <RequirePermission name="Workflows"><TestWorkflow/></RequirePermission>;

export default TestWorkflowPage;
